import {
  AccordionDetails,
  AccordionSummary,
  Autocomplete,
  Box,
  InputAdornment,
  Stack,
  TextField,
  Tooltip,
} from '@mui/material';
import { ArrowDropDown, AddCircle, Search } from '@mui/icons-material';
import {
  useCallback,
  useMemo,
  SyntheticEvent,
  useState,
  MouseEvent,
  useEffect,
  ReactNode,
} from 'react';
import CloseIcon from '@mui/icons-material/Close';
import {
  AddFiltersButton,
  AdditionalFilters,
  ButtonLabel,
  FilterAccordian,
  FilterButton,
  FilterCheckBox,
  FilterChip,
  FilterDetails,
  FilterFormControlLabel,
  FilterFormLabel,
  FilterMenu,
  FilterMenuListProps,
  FilterTextField,
  FiltersCount,
  ToolTipProps,
  searchStyles,
} from './styles';
import { useGetStructureId } from '@/hooks/useGetStructureId';
import {
  TemplateField,
  useAllPaintRegionsForFilterAndSearchPanelQuery,
  PoiStatus,
} from '@/__generated__/graphql';
import { formatEnumForDisplay } from '@/utils';
import { NOT_APPLICABLE_LABEL } from '@/constants';
import { usePaintRegionGroupsQuery } from '@/__generated__/graphql';

type PartialTemplateField = Partial<TemplateField>;

export type SelectedValues = {
  [fieldId: string]: string[];
};

type FilterAndSearchPanelProps = {
  templateFields: Array<PartialTemplateField>;
  selectedValues: SelectedValues;
  setSelectedValues: (selectedValues: SelectedValues) => void;
  handleSelectedChange: (values: SelectedValues) => void;
  showPaintRegions?: boolean;
  searchBar?: boolean;
  searchTerm?: string | undefined;
  searchPlaceholder?: string | undefined;
  setSearch?: (value: string) => void;
  setPaintRegionIds?: React.Dispatch<React.SetStateAction<string[]>>;
  filterOptions?: boolean;
  endAdornment?: ReactNode;
};

type TemplateFieldFilterProps = {
  field: PartialTemplateField | { name: string; options: string[] };
  selected: string[];
  handleFilterValuesChange: (values: string[]) => void;
};

type StaticFiltersType = { id: string; name: string; options: Array<string> };

type FilterMenuProps = {
  items: Partial<TemplateField>[];
  selected: Partial<TemplateField>[];
  setSelected: (loading: Partial<TemplateField>[]) => void;
  staticFilters?: Array<StaticFiltersType>;
};

const statusFormatted = Object.values(PoiStatus).map((status) => formatEnumForDisplay(status));

const StaticFilters = {
  verification: ['Pending', 'Verified'],
  accessibility: ['Scaffolding required', 'No Scaffolding required'],
  status: statusFormatted,
  newlyCreatedPoi: ['Yes', 'No'],
  unAssignedPoi: ['Yes', 'No'],
  mediaAttachment: ['Yes', 'No'],
  governing: ['Yes', 'No'],
};

const AddFilterMenu = ({ items, staticFilters, selected, setSelected }: FilterMenuProps) => {
  const [anchorElement, setAnchorElement] = useState<HTMLElement | undefined>(undefined);
  const open = Boolean(anchorElement);
  const handleClick = (event: MouseEvent<HTMLElement>) => {
    setAnchorElement(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorElement(undefined);
  };

  const updateSelected = (item: Partial<TemplateField>) => {
    const index = selected.findIndex((field) => field.id === item.id);
    setSelected(
      index === -1 ? [...selected, item] : selected.filter((field) => field.id !== item.id)
    );
  };

  return (
    <>
      <AddFiltersButton onClick={handleClick}>
        <AddCircle />
      </AddFiltersButton>
      <FilterMenu
        anchorEl={anchorElement}
        open={open}
        onClose={handleClose}
        MenuListProps={FilterMenuListProps}
      >
        {items.map((item) => (
          <FilterFormControlLabel
            key={item.id}
            value={item.id}
            control={
              <FilterCheckBox
                checked={Boolean(selected.some((field) => field.id === item.id))}
                onChange={() => {
                  updateSelected(item);
                }}
              />
            }
            label={item.name}
          />
        ))}
        {staticFilters?.map((item) => (
          <FilterFormControlLabel
            key={item.id}
            value={item.id}
            control={
              <FilterCheckBox
                checked={Boolean(selected.some((field) => field.id === item.id))}
                onChange={() => {
                  updateSelected(item);
                }}
              />
            }
            label={item.name}
          />
        ))}
      </FilterMenu>
    </>
  );
};

const TemplateFieldFilter = ({
  field,
  selected,
  handleFilterValuesChange,
}: TemplateFieldFilterProps) => {
  const options = useMemo(() => {
    const fieldOptions = field.options || [];
    return fieldOptions.map((option) => ({ label: option, value: option }));
  }, [field.options]);

  const selectedOptions = useMemo(() => {
    return selected.map((selectedValue) => ({ label: selectedValue, value: selectedValue }));
  }, [selected]);

  const handleSelectedOptionChange = useCallback(
    (event: SyntheticEvent<Element, Event>, values: { label: string; value: string }[]) => {
      handleFilterValuesChange(values.map((valueItem) => valueItem.value));
    },
    [handleFilterValuesChange]
  );

  return (
    <Box sx={{ marginTop: '-6px' }}>
      <FilterFormLabel>{field.name}</FilterFormLabel>
      <Autocomplete
        multiple
        disableCloseOnSelect
        data-testid={`filter-field-${field.name}`}
        size="small"
        fullWidth={false}
        options={options.filter((option) => option.value)}
        isOptionEqualToValue={(option, value) => option.value === value.value}
        defaultValue={[]}
        getOptionLabel={(option) => option.label}
        onChange={handleSelectedOptionChange}
        noOptionsText="No Options"
        disableClearable
        renderTags={(value, getTagProps) => {
          const chipLimit = 2;
          const additionalValues =
            value.length > chipLimit
              ? value
                  .slice(chipLimit)
                  .map((item) => item.label)
                  .join(', ')
              : '';
          return (
            <>
              {value.slice(0, chipLimit).map((option, index) => (
                <FilterChip {...getTagProps({ index })} label={option.label} key={option.label} />
              ))}
              <Tooltip
                title={additionalValues}
                arrow
                placement="bottom"
                componentsProps={ToolTipProps}
              >
                <AdditionalFilters>
                  {value.length > chipLimit && `+ ${value.length - chipLimit}`}
                </AdditionalFilters>
              </Tooltip>
            </>
          );
        }}
        sx={{
          minWidth: '157px',
          background: '#F6F8FB',
          '& .MuiAutocomplete-tag': {
            marginRight: '12px',
            marginLeft: '-8px',
          },
          '& fieldset': {
            border: '1px solid #B8CADD !important',
          },
        }}
        renderInput={(parameters) => (
          <FilterTextField
            {...parameters}
            placeholder={selectedOptions.length === 0 ? `Select ${field.name}` : ''}
            InputProps={{
              ...parameters.InputProps,
              sx: {
                '&::placeholder': { fontSize: '0.7rem' },
              },
            }}
          />
        )}
      />
    </Box>
  );
};

type FilterButtonProps = {
  expand: boolean;
  onClick: () => void;
  filterCount: number;
  validTemplateFields: Partial<TemplateField>[];
  staticFilters?: Array<StaticFiltersType>;
};

const DropDownFilterButton = ({
  expand,
  onClick,
  filterCount,
  validTemplateFields,
  staticFilters,
}: FilterButtonProps) => {
  return (
    <FilterButton
      data-testid="panel-filter-button"
      onClick={onClick}
      disabled={validTemplateFields.length === 0 && staticFilters?.length === 0}
    >
      <FilterDetails>
        <Stack direction="row" alignItems="center">
          <ButtonLabel>Filters</ButtonLabel>
          <FiltersCount>{filterCount}</FiltersCount>
        </Stack>
        <ArrowDropDown
          sx={{
            fontSize: '2rem',
            transform: `rotate(${expand ? '180deg' : '0deg'})`,
          }}
        />
      </FilterDetails>
    </FilterButton>
  );
};

export const FilterAndSearchPanel = ({
  templateFields,
  selectedValues,
  setSelectedValues,
  handleSelectedChange,
  showPaintRegions,
  searchBar,
  searchTerm = '',
  searchPlaceholder,
  setSearch = () => {},
  setPaintRegionIds = () => {},
  filterOptions = true,
  endAdornment,
}: FilterAndSearchPanelProps) => {
  const structureId = useGetStructureId();
  const [filtersExpanded, setFiltersExpanded] = useState<boolean>(false);
  const [paintRegionsDictionary, setPaintRegionDictionary] = useState<Record<string, string[]>>({});

  const [validTemplateFields, setValidTemplateFields] = useState<Partial<TemplateField>[]>(
    templateFields.filter((field) => field.id !== undefined)
  );

  useEffect(() => {
    setValidTemplateFields(templateFields.filter((field) => field.id !== undefined));
  }, [templateFields]);

  const [selectedFields, setSelectedFields] = useState<Partial<TemplateField>[]>(
    validTemplateFields.slice(0, 4) || []
  );

  const { data: paintRegionsData } = useAllPaintRegionsForFilterAndSearchPanelQuery({
    variables: {
      structureId,
    },
    fetchPolicy: 'cache-first',
  });

  const { data: paintRegionGroupsData } = usePaintRegionGroupsQuery({
    variables: {
      input: {
        structureId,
      },
    },
    fetchPolicy: 'cache-first',
  });

  useEffect(() => {
    const selectedFieldIds = new Set(selectedFields.map((field) => field.id));
    const updatedValues = { ...selectedValues };
    Object.keys(selectedValues).forEach((key) => {
      if (!selectedFieldIds.has(key)) {
        delete updatedValues[key];
      }
    });
    setSelectedValues(updatedValues);
    // TODO: Fix exhaustive dependencies
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFields]);

  const paintRegionOptions = useMemo(() => {
    if (paintRegionGroupsData?.paintRegionGroups?.length === 0) {
      return [
        {
          name: 'Paint Block',
          options: [
            ...(paintRegionsData?.allPaintRegions.map((paintRegion) => paintRegion.name) ?? []),
            NOT_APPLICABLE_LABEL,
          ],
        },
      ];
    }
    return paintRegionGroupsData?.paintRegionGroups.map((group) => {
      return {
        name: group.name,
        options: [
          ...(paintRegionsData?.allPaintRegions
            .filter((paintRegion) => paintRegion.paintRegionGroupId === group.id)
            .map((paintRegion) => paintRegion.name) ?? []),
          NOT_APPLICABLE_LABEL,
        ],
      };
    });
  }, [paintRegionGroupsData, paintRegionsData?.allPaintRegions]);

  const handleFilterValuesChange = useCallback(
    (fieldId: string) => (values: string[]) => {
      handleSelectedChange({
        ...selectedValues,
        [fieldId]: values,
      });
    },
    [handleSelectedChange, selectedValues]
  );

  const handlePaintBlockChange = useCallback(
    (fieldId: string) => (values: string[]) => {
      const currentGroupIds =
        paintRegionsData?.allPaintRegions
          ?.filter((paintRegion) => values.includes(paintRegion.name))
          ?.map((paintRegion) => paintRegion.id) ?? [];

      setPaintRegionIds(
        Object.values({ ...paintRegionsDictionary, [fieldId]: currentGroupIds }).flat()
      );

      // Add NOT_APPLICABLE if selected
      if (values.includes(NOT_APPLICABLE_LABEL)) {
        setPaintRegionIds((prev) => [...prev, NOT_APPLICABLE_LABEL]);
      }

      setPaintRegionDictionary((prev) => ({
        ...prev,
        [fieldId]: currentGroupIds,
      }));
    },
    [paintRegionsData?.allPaintRegions, setPaintRegionIds, paintRegionsDictionary]
  );

  const verificationOptions = useMemo(() => {
    return {
      id: 'isVerified',
      name: 'Verification',
      options: StaticFilters.verification,
    };
  }, []);

  const governingOptions = useMemo(() => {
    return {
      id: 'isGoverning',
      name: 'Governing',
      options: StaticFilters.governing,
    };
  }, []);

  const accessibilityOptions = useMemo(() => {
    return {
      id: 'accessibility',
      name: 'Accessibility',
      options: StaticFilters.accessibility,
    };
  }, []);

  const statusOptions = useMemo(() => {
    return {
      id: 'status',
      name: 'Status',
      options: StaticFilters.status,
    };
  }, []);

  const newlyCreatedPoi = useMemo(() => {
    return {
      id: 'isManuallyCreated',
      name: 'Imported POI',
      options: StaticFilters.newlyCreatedPoi,
    };
  }, []);

  const unAssignedPoi = useMemo(() => {
    return {
      id: 'unAssignedPoi',
      name: '3D UnAssigned POI',
      options: StaticFilters.unAssignedPoi,
    };
  }, []);

  const mediaAttachment = useMemo(() => {
    return {
      id: 'mediaAttachment',
      name: 'Media Attachment',
      options: StaticFilters.mediaAttachment,
    };
  }, []);

  const staticFilters = useMemo(() => {
    return [
      governingOptions,
      verificationOptions,
      accessibilityOptions,
      statusOptions,
      newlyCreatedPoi,
      unAssignedPoi,
      mediaAttachment,
    ];
  }, [
    governingOptions,
    verificationOptions,
    accessibilityOptions,
    statusOptions,
    newlyCreatedPoi,
    unAssignedPoi,
    mediaAttachment,
  ]);

  useEffect(() => {
    if (validTemplateFields.length < 4) {
      setSelectedFields([...validTemplateFields, ...staticFilters]);
    }
  }, [validTemplateFields, staticFilters]);
  return (
    <Box marginBottom={3}>
      <Stack direction="row" alignItems="center" gap={2} justifyContent="space-between">
        {filterOptions && (
          <DropDownFilterButton
            onClick={() => {
              setFiltersExpanded(!filtersExpanded);
            }}
            expand={filtersExpanded}
            filterCount={Object.keys(selectedValues).length}
            validTemplateFields={validTemplateFields}
            staticFilters={staticFilters}
          />
        )}
        {searchBar && (
          <TextField
            fullWidth
            data-testid="panel-search-input"
            size="small"
            sx={searchStyles}
            placeholder={searchPlaceholder ?? 'Search'}
            value={searchTerm}
            onChange={(event) => setSearch(event.target.value)}
            InputProps={{
              startAdornment: <Search htmlColor="#6E8198" sx={{ marginRight: '8px' }} />,
              endAdornment: searchTerm.length > 0 && (
                <InputAdornment position="end">
                  <CloseIcon sx={{ cursor: 'pointer' }} onClick={() => setSearch('')} />
                </InputAdornment>
              ),
            }}
          />
        )}
        {endAdornment && endAdornment}
      </Stack>
      {filterOptions && (
        <FilterAccordian expanded={filtersExpanded} square>
          <AccordionSummary
            id="panel-header"
            aria-controls="panel-content"
            sx={{ display: 'none' }}
          />
          <AccordionDetails sx={{ padding: '12px 16px' }}>
            <Stack
              direction="row"
              sx={{
                flexWrap: 'wrap',
                gap: 2,
              }}
              alignItems="flex-end"
            >
              {showPaintRegions &&
                paintRegionOptions?.map((group) => (
                  <TemplateFieldFilter
                    field={group}
                    selected={paintRegionsDictionary[group.name] ?? []}
                    handleFilterValuesChange={handlePaintBlockChange(group.name)}
                  />
                ))}
              {selectedFields.map((field) => (
                <>
                  {!!field.id && (
                    <TemplateFieldFilter
                      key={field.id}
                      field={field}
                      selected={selectedValues[field.id] || []}
                      handleFilterValuesChange={handleFilterValuesChange(field.id)}
                    />
                  )}
                </>
              ))}
              {validTemplateFields.length > 3 && (
                <AddFilterMenu
                  items={validTemplateFields.slice(4)}
                  selected={selectedFields}
                  setSelected={setSelectedFields}
                  staticFilters={staticFilters}
                />
              )}
            </Stack>
          </AccordionDetails>
        </FilterAccordian>
      )}
    </Box>
  );
};
